import type { Lang, Localized } from './i18n.js';
import type { ItemConfig } from './menu.js';

/**
 * Demo content in both languages. The seed stores the French text in the database (as before),
 * and the apps translate it at display time with localizeDemo(). Text typed later by a restaurant
 * (a new dish, a new promo) is not in this list and is shown exactly as typed.
 */
const L = (en: string, fr: string): Localized => ({ en, fr });

export interface DemoOption {
  id: string;
  name: Localized;
  priceDeltaCents: number;
  isDefault?: boolean;
}
export interface DemoGroup {
  id: string;
  name: Localized;
  type: 'single' | 'multi';
  required: boolean;
  maxSelections?: number;
  options: DemoOption[];
}
export interface DemoItem {
  name: Localized;
  description: Localized;
  priceCents: number;
  image: string;
  groups?: DemoGroup[];
  popular?: boolean;
  tags?: Localized[];
}
export interface DemoCategory {
  name: Localized;
  icon: string;
  items: DemoItem[];
}

const pizzaGroups: DemoGroup[] = [
  {
    id: 'size',
    name: L('Size', 'Taille'),
    type: 'single',
    required: true,
    options: [
      { id: 'small', name: L('Small 10"', 'Petite 10 po'), priceDeltaCents: 0 },
      { id: 'medium', name: L('Medium 12"', 'Moyenne 12 po'), priceDeltaCents: 400, isDefault: true },
      { id: 'large', name: L('Large 14"', 'Grande 14 po'), priceDeltaCents: 800 },
    ],
  },
  {
    id: 'crust',
    name: L('Crust', 'Type de croûte'),
    type: 'single',
    required: true,
    options: [
      { id: 'classic', name: L('Classic', 'Classique'), priceDeltaCents: 0, isDefault: true },
      { id: 'thin', name: L('Thin', 'Mince'), priceDeltaCents: 0 },
      { id: 'thick', name: L('Thick', 'Épaisse'), priceDeltaCents: 100 },
      { id: 'gluten-free', name: L('Gluten-free', 'Sans gluten'), priceDeltaCents: 300 },
    ],
  },
  {
    id: 'extras',
    name: L('Extra toppings', 'Ingrédients supplémentaires'),
    type: 'multi',
    required: false,
    maxSelections: 6,
    options: [
      { id: 'cheese', name: L('Extra cheese', 'Extra fromage'), priceDeltaCents: 250 },
      { id: 'pepperoni', name: L('Pepperoni', 'Pepperoni'), priceDeltaCents: 250 },
      { id: 'bacon', name: L('Bacon', 'Bacon'), priceDeltaCents: 250 },
      { id: 'mushrooms', name: L('Mushrooms', 'Champignons'), priceDeltaCents: 150 },
      { id: 'peppers', name: L('Peppers', 'Poivrons'), priceDeltaCents: 150 },
      { id: 'onions', name: L('Red onions', 'Oignons rouges'), priceDeltaCents: 150 },
      { id: 'olives', name: L('Olives', 'Olives'), priceDeltaCents: 150 },
      { id: 'arugula', name: L('Arugula', 'Roquette'), priceDeltaCents: 200 },
    ],
  },
];

const poutineGroups: DemoGroup[] = [
  {
    id: 'size',
    name: L('Size', 'Format'),
    type: 'single',
    required: true,
    options: [
      { id: 'regular', name: L('Regular', 'Régulière'), priceDeltaCents: 0, isDefault: true },
      { id: 'large', name: L('Large', 'Grande'), priceDeltaCents: 350 },
    ],
  },
  {
    id: 'extras',
    name: L('Extras', 'Extras'),
    type: 'multi',
    required: false,
    options: [
      { id: 'bacon', name: L('Bacon', 'Bacon'), priceDeltaCents: 250 },
      { id: 'sausage', name: L('Italian sausage', 'Saucisse italienne'), priceDeltaCents: 300 },
      { id: 'curds', name: L('Extra cheese curds', 'Extra fromage en grains'), priceDeltaCents: 250 },
    ],
  },
];

const subGroups: DemoGroup[] = [
  {
    id: 'size',
    name: L('Length', 'Longueur'),
    type: 'single',
    required: true,
    options: [
      { id: '6', name: L('6 inches', '6 pouces'), priceDeltaCents: 0, isDefault: true },
      { id: '12', name: L('12 inches', '12 pouces'), priceDeltaCents: 550 },
    ],
  },
  {
    id: 'bread',
    name: L('Bread', 'Pain'),
    type: 'single',
    required: true,
    options: [
      { id: 'white', name: L('White', 'Blanc'), priceDeltaCents: 0, isDefault: true },
      { id: 'wheat', name: L('Whole wheat', 'Blé entier'), priceDeltaCents: 0 },
    ],
  },
  {
    id: 'extras',
    name: L('Extras', 'Extras'),
    type: 'multi',
    required: false,
    options: [
      { id: 'cheese', name: L('Extra cheese', 'Extra fromage'), priceDeltaCents: 200 },
      { id: 'peppers', name: L('Hot peppers', 'Piments forts'), priceDeltaCents: 100 },
    ],
  },
];

const saladGroups: DemoGroup[] = [
  {
    id: 'dressing',
    name: L('Dressing', 'Vinaigrette'),
    type: 'single',
    required: true,
    options: [
      { id: 'italian', name: L('Italian', 'Italienne'), priceDeltaCents: 0, isDefault: true },
      { id: 'caesar', name: L('Caesar', 'César'), priceDeltaCents: 0 },
      { id: 'balsamic', name: L('Balsamic', 'Balsamique'), priceDeltaCents: 0 },
    ],
  },
  {
    id: 'protein',
    name: L('Add a protein', 'Ajouter une protéine'),
    type: 'single',
    required: false,
    options: [
      { id: 'chicken', name: L('Grilled chicken', 'Poulet grillé'), priceDeltaCents: 400 },
      { id: 'shrimp', name: L('Shrimp', 'Crevettes'), priceDeltaCents: 550 },
    ],
  },
];

const sodaGroups: DemoGroup[] = [
  {
    id: 'format',
    name: L('Size', 'Format'),
    type: 'single',
    required: true,
    options: [
      { id: 'can', name: L('355 mL can', 'Canette 355 ml'), priceDeltaCents: 0, isDefault: true },
      { id: 'bottle', name: L('2 L bottle', 'Bouteille 2 L'), priceDeltaCents: 250 },
    ],
  },
];

const wingsGroups: DemoGroup[] = [
  {
    id: 'qty',
    name: L('Quantity', 'Quantité'),
    type: 'single',
    required: true,
    options: [
      { id: '8', name: L('8 wings', '8 ailes'), priceDeltaCents: 0, isDefault: true },
      { id: '16', name: L('16 wings', '16 ailes'), priceDeltaCents: 1000 },
    ],
  },
  {
    id: 'sauce',
    name: L('Sauce', 'Sauce'),
    type: 'single',
    required: true,
    options: [
      { id: 'bbq', name: L('BBQ', 'BBQ'), priceDeltaCents: 0, isDefault: true },
      { id: 'buffalo', name: L('Buffalo', 'Buffalo'), priceDeltaCents: 0 },
      { id: 'honey-garlic', name: L('Honey garlic', 'Miel et ail'), priceDeltaCents: 0 },
    ],
  },
];

const CLASSICS = L('Classics', 'Classiques');
const VEGETARIAN = L('Vegetarian', 'Végétariennes');
const SPECIALTIES = L('Specialties', 'Spéciales');

export const FORNAVIA_DEMO_MENU: DemoCategory[] = [
  {
    name: L('Pizzas', 'Pizzas'),
    icon: 'pizza',
    items: [
      { name: L('Margherita', 'Margherita'), description: L('Tomato sauce, fior di latte mozzarella, fresh basil, olive oil', 'Sauce tomate, mozzarella fior di latte, basilic frais, huile d’olive'), priceCents: 1495, image: 'pizza-margherita', groups: pizzaGroups, popular: true, tags: [CLASSICS, VEGETARIAN] },
      { name: L('Pepperoni', 'Pepperoni'), description: L('Tomato sauce, mozzarella, pepperoni, oregano', 'Sauce tomate, mozzarella, pepperoni, origan'), priceCents: 1595, image: 'pizza-pepperoni', groups: pizzaGroups, popular: true, tags: [CLASSICS] },
      { name: L('Veggie', 'Végétarienne'), description: L('Peppers, mushrooms, red onions, olives, tomatoes', 'Poivrons, champignons, oignons rouges, olives, tomates'), priceCents: 1695, image: 'pizza-veggie', groups: pizzaGroups, tags: [VEGETARIAN] },
      { name: L('All dressed', 'Toute garnie'), description: L('Pepperoni, mushrooms, peppers, mozzarella', 'Pepperoni, champignons, poivrons, mozzarella'), priceCents: 1795, image: 'pizza-supreme', groups: pizzaGroups, tags: [CLASSICS] },
      { name: L('Quattro Formaggi', 'Quattro Formaggi'), description: L('Mozzarella, gorgonzola, parmigiano, fontina', 'Mozzarella, gorgonzola, parmigiano, fontina'), priceCents: 1795, image: 'pizza-cheese', groups: pizzaGroups, tags: [SPECIALTIES, VEGETARIAN] },
      { name: L('Prosciutto', 'Prosciutto'), description: L('Tomato sauce, mozzarella, Parma ham, arugula, shaved parmesan', 'Sauce tomate, mozzarella, jambon de Parme, roquette, copeaux de parmesan'), priceCents: 1895, image: 'pizza-prosciutto', groups: pizzaGroups, tags: [SPECIALTIES] },
      { name: L('Calabrese', 'Calabrese'), description: L('Tomato sauce, nduja, spicy salami, chili peppers', 'Sauce tomate, nduja, salami piquant, piments'), priceCents: 1895, image: 'pizza-calabrese', groups: pizzaGroups, tags: [SPECIALTIES] },
      { name: L('BBQ chicken', 'BBQ Poulet'), description: L('BBQ sauce, grilled chicken, red onions, cilantro', 'Sauce BBQ, poulet grillé, oignons rouges, coriandre'), priceCents: 1795, image: 'pizza-bbq', groups: pizzaGroups, tags: [SPECIALTIES] },
    ],
  },
  {
    name: L('Poutines', 'Poutines'),
    icon: 'poutine',
    items: [
      { name: L('Classic poutine', 'Poutine classique'), description: L('House-cut fries, cheese curds, brown gravy', 'Frites maison, fromage en grains, sauce brune'), priceCents: 1095, image: 'poutine', groups: poutineGroups, popular: true },
      { name: L('Italian poutine', 'Poutine italienne'), description: L('Bolognese sauce, melted mozzarella', 'Sauce bolognaise, mozzarella gratinée'), priceCents: 1395, image: 'poutine-italian', groups: poutineGroups },
      { name: L('Chicken poutine', 'Poutine au poulet'), description: L('Pulled chicken, green peas, brown gravy', 'Poulet effiloché, petits pois, sauce brune'), priceCents: 1395, image: 'poutine', groups: poutineGroups },
      { name: L('Pepperoni poutine', 'Poutine pepperoni'), description: L('Crispy pepperoni, cheese curds, brown gravy', 'Pepperoni croustillant, fromage en grains, sauce brune'), priceCents: 1295, image: 'poutine-italian', groups: poutineGroups },
    ],
  },
  {
    name: L('Subs', 'Sous-marins'),
    icon: 'sub',
    items: [
      { name: L('Italian sub', 'Sous-marin italien'), description: L('Salami, capicollo, provolone, lettuce, tomatoes', 'Salami, capicollo, provolone, laitue, tomates'), priceCents: 1095, image: 'sub', groups: subGroups },
      { name: L('Steak sub', 'Sous-marin steak'), description: L('Sliced steak, onions, peppers, melted cheese', 'Steak émincé, oignons, poivrons, fromage fondu'), priceCents: 1295, image: 'sub-steak', groups: subGroups, popular: true },
      { name: L('Chicken sub', 'Sous-marin poulet'), description: L('Grilled chicken, pesto mayo, lettuce, tomatoes', 'Poulet grillé, mayo au pesto, laitue, tomates'), priceCents: 1195, image: 'sub', groups: subGroups },
      { name: L('Veggie sub', 'Sous-marin végé'), description: L('Grilled vegetables, provolone, pesto', 'Légumes grillés, provolone, pesto'), priceCents: 1045, image: 'sub-steak', groups: subGroups },
    ],
  },
  {
    name: L('Starters', 'Entrées'),
    icon: 'starter',
    items: [
      { name: L('Chicken wings', 'Ailes de poulet'), description: L('Crispy wings with dipping sauce', 'Ailes croustillantes et trempette'), priceCents: 1395, image: 'wings', groups: wingsGroups, popular: true },
      { name: L('Garlic bread', 'Pain à l’ail'), description: L('House-made bread, garlic butter, parsley', 'Pain maison, beurre à l’ail, persil'), priceCents: 595, image: 'garlic-bread' },
      { name: L('Cheesy garlic bread', 'Pain à l’ail gratiné'), description: L('Garlic bread, melted mozzarella', 'Pain à l’ail, mozzarella gratinée'), priceCents: 795, image: 'garlic-bread' },
      { name: L('Mozzarella sticks', 'Bâtonnets de mozzarella'), description: L('6 sticks, marinara sauce', '6 bâtonnets, sauce marinara'), priceCents: 995, image: 'mozza-sticks' },
      { name: L('House-cut fries', 'Frites maison'), description: L('Quebec potatoes, sea salt', 'Pommes de terre du Québec, sel de mer'), priceCents: 495, image: 'fries' },
    ],
  },
  {
    name: L('Salads', 'Salades'),
    icon: 'salad',
    items: [
      { name: L('Caesar salad', 'Salade César'), description: L('Romaine, croutons, parmesan, bacon', 'Romaine, croûtons, parmesan, bacon'), priceCents: 1095, image: 'salad-caesar', groups: saladGroups },
      { name: L('Greek salad', 'Salade grecque'), description: L('Tomatoes, cucumbers, feta, Kalamata olives', 'Tomates, concombres, feta, olives Kalamata'), priceCents: 1095, image: 'salad-greek', groups: saladGroups },
      { name: L('Garden salad', 'Salade du jardin'), description: L('Mixed greens, seasonal vegetables', 'Laitues mixtes, légumes de saison'), priceCents: 895, image: 'salad-greek', groups: saladGroups },
      { name: L('Caprese salad', 'Salade caprese'), description: L('Tomatoes, bocconcini, basil, balsamic', 'Tomates, bocconcini, basilic, balsamique'), priceCents: 1195, image: 'salad-caesar', groups: saladGroups },
    ],
  },
  {
    name: L('Drinks', 'Boissons'),
    icon: 'drink',
    items: [
      // Generic names: the demo never shows real brands.
      { name: L('Cola', 'Cola'), description: L('Soft drink', 'Boisson gazeuse'), priceCents: 250, image: 'soda-red', groups: sodaGroups },
      { name: L('Lemon-lime soda', 'Soda citron-lime'), description: L('Lemon-lime soft drink', 'Boisson gazeuse citron-lime'), priceCents: 250, image: 'soda-green', groups: sodaGroups },
      { name: L('Sparkling water', 'Eau pétillante'), description: L('Sparkling mineral water, 500 mL', 'Eau minérale pétillante 500 ml'), priceCents: 350, image: 'water' },
      { name: L('House lemonade', 'Limonade maison'), description: L('Fresh-squeezed lemons, mint', 'Citrons pressés, menthe'), priceCents: 450, image: 'lemonade' },
      { name: L('Iced tea', 'Thé glacé'), description: L('Brewed black tea, peach', 'Thé noir infusé, pêche'), priceCents: 350, image: 'lemonade' },
    ],
  },
  {
    name: L('Desserts', 'Desserts'),
    icon: 'dessert',
    items: [
      { name: L('Tiramisu', 'Tiramisu'), description: L('Mascarpone, espresso, cocoa', 'Mascarpone, espresso, cacao'), priceCents: 795, image: 'tiramisu', popular: true },
      { name: L('Cannoli', 'Cannoli'), description: L('2 ricotta and pistachio cannoli', '2 cannoli à la ricotta et pistaches'), priceCents: 695, image: 'cannoli' },
      { name: L('Chocolate cake', 'Gâteau au chocolat'), description: L('Dark chocolate fondant', 'Fondant au chocolat noir'), priceCents: 750, image: 'chocolate-cake' },
      { name: L('Panna cotta', 'Panna cotta'), description: L('Mixed berry coulis', 'Coulis de petits fruits'), priceCents: 695, image: 'panna-cotta' },
    ],
  },
];

export const BRASVIK_DEMO_MENU: DemoCategory[] = [
  {
    name: L('Burgers', 'Burgers'),
    icon: 'burger',
    items: [
      { name: L('Classic burger', 'Burger classique'), description: L('Beef, cheddar, lettuce, tomato', 'Bœuf, cheddar, laitue, tomate'), priceCents: 1295, image: 'burger' },
      { name: L('Bacon burger', 'Burger bacon'), description: L('Beef, bacon, smoked cheddar', 'Bœuf, bacon, cheddar fumé'), priceCents: 1495, image: 'burger' },
      { name: L('Veggie burger', 'Burger végé'), description: L('Chickpea patty', 'Galette de pois chiches'), priceCents: 1295, image: 'burger' },
      { name: L('Chicken burger', 'Burger poulet'), description: L('Crispy chicken, spicy mayo', 'Poulet croustillant, mayo épicée'), priceCents: 1395, image: 'burger' },
    ],
  },
  {
    name: L('Sides', 'Accompagnements'),
    icon: 'starter',
    items: [
      { name: L('Fries', 'Frites'), description: L('House-cut fries', 'Frites maison'), priceCents: 495, image: 'fries' },
      { name: L('Onion rings', 'Rondelles d’oignon'), description: L('House-made breading', 'Panure maison'), priceCents: 595, image: 'fries' },
      { name: L('Poutine', 'Poutine'), description: L('Classic', 'Classique'), priceCents: 995, image: 'poutine' },
      { name: L('Coleslaw', 'Salade de chou'), description: L('Creamy', 'Crémeuse'), priceCents: 395, image: 'salad-greek' },
    ],
  },
];

/** Restaurant texts and labels the seed writes in French. */
export const DEMO_TEXT = {
  fornaviaDescription: L(
    'Authentic pizzas, delivered with passion. House-made dough fermented for 48 hours, baked in a stone oven.',
    'Des pizzas authentiques, livrées avec passion. Pâte maison fermentée 48 h, four à pierre.',
  ),
  fornaviaPromoTitle: L('2 medium pizzas for $29.99', '2 pizzas moyennes pour 29,99 $'),
  fornaviaPromoSubtitle: L('Delivery only, Monday to Thursday', 'En livraison seulement, du lundi au jeudi'),
  brasvikDescription: L('Smash burgers and house-cut fries.', 'Burgers smashés et frites maison.'),
  platformAdminName: L('Platform Admin', 'Admin Plateforme'),
} satisfies Record<string, Localized>;

/** Seed shape (one language): what the database receives. */
export interface SeedMenuItem {
  name: string;
  description: string;
  priceCents: number;
  image: string;
  groups?: ItemConfig['groups'];
  popular?: boolean;
  tags?: string[];
}

export function demoMenuIn(menu: DemoCategory[], lang: Lang): { name: string; icon: string; items: SeedMenuItem[] }[] {
  return menu.map((c) => ({
    name: c.name[lang],
    icon: c.icon,
    items: c.items.map((i) => ({
      name: i.name[lang],
      description: i.description[lang],
      priceCents: i.priceCents,
      image: i.image,
      groups: i.groups?.map((g) => ({
        id: g.id,
        name: g.name[lang],
        type: g.type,
        required: g.required,
        ...(g.maxSelections ? { maxSelections: g.maxSelections } : {}),
        options: g.options.map((o) => ({ id: o.id, name: o.name[lang], priceDeltaCents: o.priceDeltaCents, ...(o.isDefault ? { isDefault: true } : {}) })),
      })),
      popular: i.popular,
      tags: i.tags?.map((t) => t[lang]),
    })),
  }));
}

/** Every bilingual pair above (used to build the lookup and by the tests). */
export function allDemoPairs(): Localized[] {
  const out: Localized[] = Object.values(DEMO_TEXT);
  for (const cat of [...FORNAVIA_DEMO_MENU, ...BRASVIK_DEMO_MENU]) {
    out.push(cat.name);
    for (const it of cat.items) {
      out.push(it.name, it.description, ...(it.tags ?? []));
      for (const g of it.groups ?? []) out.push(g.name, ...g.options.map((o) => o.name));
    }
  }
  return out;
}

let index: Map<string, Localized> | null = null;
function lookup(): Map<string, Localized> {
  if (index) return index;
  const m = new Map<string, Localized>();
  const pairs = allDemoPairs();
  // French keys win: a French text must always stay as is in French.
  for (const p of pairs) if (!m.has(p.fr)) m.set(p.fr, p);
  for (const p of pairs) if (!m.has(p.en)) m.set(p.en, p);
  index = m;
  return m;
}

/** Shows a demo text (menu, option, category, promo…) in the requested language; other text is returned unchanged. */
export function localizeDemo(text: string, lang: Lang): string;
export function localizeDemo(text: string | null | undefined, lang: Lang): string | null | undefined;
export function localizeDemo(text: string | null | undefined, lang: Lang) {
  if (!text) return text;
  return lookup().get(text)?.[lang] ?? text;
}
